import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { LinkIcon } from '@heroicons/react/24/solid';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';

// Logo icon reused from HomePage
const LogoIcon = () => (
  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1.33325V14.6666" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 4.66675L8.00001 1.33341L4 4.66675" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M4 11.3333L8.00001 14.6666L12 11.3333" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
);

// Utility function to get domain from URL
const getDomain = (url) => {
  try {
    const domain = new URL(url).hostname;
    return domain.replace('www.', '');
  } catch (e) {
    return url;
  }
};

// Componente reescrito para animar la escritura del texto de la respuesta
const TypewriterText = ({ text, onComplete }) => {
  // Usamos useState solo para el texto mostrado
  const [displayedText, setDisplayedText] = useState('');
  
  // Usamos useRef para gestionar el estado interno que no debería causar re-renderizados
  const animationState = useRef({
    isComplete: false,
    currentIndex: 0,
    originalText: text
  });
  
  // Efecto principal para manejar la animación
  useEffect(() => {
    console.log("[Typewriter] Animation setup - text length:", text.length);
    
    // Skip animation for empty text
    if (!text) {
      console.log("[Typewriter] Empty text, skipping animation.");
      if (onComplete) onComplete();
      return;
    }
    
    // No needed to reset if already completed
    if (animationState.current.isComplete) {
      console.log("[Typewriter] Animation already complete, not resetting.");
      return;
    }
    
    // Initialize animation
    animationState.current.originalText = text;
    animationState.current.currentIndex = 0;
    animationState.current.isComplete = false;
    setDisplayedText('');
    
    // Setup animation tick function
    const tick = () => {
      if (animationState.current.currentIndex < text.length) {
        const nextChar = text[animationState.current.currentIndex];
        animationState.current.currentIndex += 1;
        
        setDisplayedText(prev => prev + nextChar);
        
        // Schedule next tick
        animationState.current.timeoutId = setTimeout(tick, 15);
      } else {
        // Animation complete
        console.log("[Typewriter] Animation complete, calling onComplete");
        animationState.current.isComplete = true;
        if (onComplete) onComplete();
      }
    };
    
    // Start the animation
    console.log("[Typewriter] Starting animation");
    animationState.current.timeoutId = setTimeout(tick, 15);
    
    // Cleanup
    return () => {
      if (animationState.current.timeoutId) {
        clearTimeout(animationState.current.timeoutId);
      }
    };
  }, [text, onComplete]); // Only re-run if text or onComplete actually changes
  
  return (
    <p className="text-gray-700 leading-relaxed">
      {displayedText}
      <span className={animationState.current.isComplete ? "hidden" : "animate-pulse"}>▋</span>
    </p>
  );
};

const ResultItem = ({ result, onAnimationComplete }) => {
  const { t } = useTranslation();
  
  console.log(`[ResultItem] Rendering ID: ${result.id}, isNew: ${result.isNew}, Status: ${result.status}, Has Summary: ${!!result.summary}`);

  // Custom styles for markdown output
  const markdownStyles = {
    p: 'text-gray-700 leading-relaxed',
    strong: 'font-bold text-gray-900',
    em: 'italic text-gray-800',
    ul: 'list-disc pl-5 my-2',
    ol: 'list-decimal pl-5 my-2',
    li: 'text-gray-700 my-1',
  };

  // Memoize the summary text
  const safeSummary = useMemo(() => {
    return result.summary === null ? '' : String(result.summary || '');
  }, [result.summary]);
  
  // Memoize the completion handler
  const handleAnimationComplete = useCallback(() => {
    console.log(`[ResultItem] Animation complete for ID: ${result.id}`);
    onAnimationComplete(result.id);
  }, [result.id, onAnimationComplete]);

  return (
    <div className={`flex w-full max-w-3xl mx-auto mb-5 ${result.isNew ? 'animate-fadeIn' : ''}`}>
      <LogoIcon />
      <div className="flex-1 ml-4">
        <div className="bg-gray-100 rounded-2xl px-5 py-4">
          <div className="flex flex-col">
            <div className="flex items-center mb-1.5">
              <span className="text-sm font-medium text-gray-700">
                {getDomain(result.url)}
              </span>
              <a 
                href={result.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-2 text-xs text-gray-500 hover:text-black truncate max-w-xs inline-flex items-center"
              >
                <LinkIcon className="h-3 w-3 mr-1" />
                <span className="truncate">{t('history.viewOriginal')}</span>
              </a>
            </div>
            
            <div className="text-[15px]">
              {result.status === 'unreachable' || !result.summary ? (
                <p className="text-gray-700 leading-relaxed italic">
                  {result.message || t('results.unreachableContent')}
                </p>
              ) : result.isNew ? (
                <TypewriterText 
                  text={safeSummary}
                  onComplete={handleAnimationComplete}
                />
              ) : (
                <ReactMarkdown 
                  className="text-gray-700 leading-relaxed"
                  components={{
                    p: ({node, ...props}) => <p className={markdownStyles.p} {...props} />,
                    strong: ({node, ...props}) => <strong className={markdownStyles.strong} {...props} />,
                    em: ({node, ...props}) => <em className={markdownStyles.em} {...props} />,
                    ul: ({node, ...props}) => <ul className={markdownStyles.ul} {...props} />,
                    ol: ({node, ...props}) => <ol className={markdownStyles.ol} {...props} />,
                    li: ({node, ...props}) => <li className={markdownStyles.li} {...props} />,
                  }}
                >
                  {safeSummary}
                </ReactMarkdown>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultItem; 